import { t } from 'i18next'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { LazyLoadComponent } from 'react-lazy-load-image-component'

import { Box, Card, CardContent, Divider, Link, Typography } from '@mui/material'

import { GRTooltip } from '../../../components/GRTooltip/GRTooltip'
import { useCurrentUserLanguage } from '../../account/hooks/useCurrentUserLanguage'
import { GameIcon } from '../../game'
import { useCurrentMarket } from '../../markets'
import { getMarketIsosByUserLanguage } from '../../markets/utils/utils'
import { getAttributeNameByModel } from '../hooks/useIconAttributes'
import { RankType, VisualAnalysisWithRank, VisualsAttribute, VisualsGroupedData, VisualSortType } from '../types'
import { getIconComparator } from '../util/comparators'
import { AppStoreGenreMap } from '../util/visualStats'
import './VisualIconsList.scss'

type TooltipProps = {
  attributes: VisualsAttribute[]
  item: VisualAnalysisWithRank
  marketIso: string
  rankText: string
}

interface VisualIconsListProps {
  attributes: VisualsAttribute[]
  data: VisualsGroupedData
  genreId: string
  rankType: RankType
  onSelectAnalysis: (analysis: VisualAnalysisWithRank) => void
  sortBy: VisualSortType
  top: number
}

type TooltipValueProps = {
  attribute: VisualsAttribute
  item: VisualAnalysisWithRank
}

const TooltipValue: React.FC<TooltipValueProps> = ({ attribute, item }) => {
  const attrName = getAttributeNameByModel(attribute.model)
  const attrs = item[attrName]
  let value: number = (attrs && attrs[attribute.name]) || 0

  // For color groups, `useIconGrouper`'s color-percentage filter accepts a game when either
  // `colorGroups[name]` OR `colors[name]` meets the threshold. Mirror that fallback here so the
  // tooltip stays consistent with the filtered set and we don't leave an orphan divider with no
  // color-group value rendered for games that only carry the value under `colors`. (GR-1752)
  if (value <= 0 && attribute.model === 'colorGroups') {
    const colorsValue = (item.colors && item.colors[attribute.name]) || 0
    if (colorsValue > 0) {
      value = colorsValue
    }
  }

  // Color values from the API may be either a 0-1 fraction or a 0-100 percent.
  // `useIconGrouper` normalizes to 0-100; here we render `value * 100`, so normalize to 0-1
  // first to avoid showing e.g. `5000%` for values returned as 0-100. Only applies to color
  // models since other attribute models are already 0-1 fractions.
  if (attribute.model === 'colorGroups' && value > 1) {
    value = value / 100
  }

  return value <= 0 ? null : (
    <small style={{ display: 'block', textTransform: 'capitalize' }}>
      {attribute.name}: {Math.round(value * 100)}%
    </small>
  )
}

const Tooltip: React.FC<TooltipProps> = ({ attributes, item, marketIso, rankText }) => {
  const userLanguage = useCurrentUserLanguage()
  const marketIsosByUserLanguage = getMarketIsosByUserLanguage(userLanguage)

  // Rule 1: Use item.gameNames[marketIso] if it exists
  let gameName = item.gameNames[marketIso]

  // Rule 2: If item.gameNames[marketIso] is missing, try to match using marketIsosByUserLanguage
  if (!gameName) {
    const matchingIso = marketIsosByUserLanguage.find((iso) => item.gameNames[iso])
    gameName = matchingIso ? item.gameNames[matchingIso] : ''
  }

  // Rule 3: If still no match, use the first item from item.gameNames
  if (!gameName) {
    gameName = Object.values(item.gameNames)[0]
  }

  // Rule 4: If still nothing, use 'N/A'
  if (!gameName) {
    gameName = t('common:not_available_shorthand')
  }

  return (
    <>
      <strong>{gameName}</strong>
      <br />
      <small>{rankText}</small>
      {!!attributes?.length && (
        <>
          <Divider sx={{ my: 1 }} />
          {attributes.map((attribute) => (
            <TooltipValue key={attribute.name} attribute={attribute} item={item} />
          ))}
        </>
      )}
    </>
  )
}

const VisualIconsList: React.FC<VisualIconsListProps> = ({ attributes, data, genreId, onSelectAnalysis, rankType, sortBy, top }) => {
  const { t } = useTranslation()
  const comparator = getIconComparator(sortBy, rankType, attributes)
  const { currentMarketIso: marketIso } = useCurrentMarket()
  return (
    <Box className="VisualIconsList">
      {['top', 'out'].map((group) => {
        const rankText = AppStoreGenreMap[genreId] + ' / ' + t(`common:${rankType === 'free' ? 'sustained_download_rank' : 'sustained_grossing_rank'}`)
        const items = data[group as keyof VisualsGroupedData]
        return (
          <Card key={'group-' + group} sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h3" mb={2}>
                {t('common:' + (group === 'out' ? 'outside_' : '') + 'top_' + top)} {rankText}
              </Typography>
              {!items.length && (
                <Typography mb={4} mt={-1} variant="body2">
                  {t('visuals-explorer:visual_attribute_modal_no_games_found')}
                </Typography>
              )}
              {items.sort(comparator).map((item, index) => (
                <Box className="VisualIconsList__gameList" key={index}>
                  <GRTooltip
                    content={
                      <Tooltip
                        marketIso={marketIso}
                        item={item}
                        rankText={rankText + ': ' + (rankType === 'free' ? item.sdrank : item.srank)}
                        attributes={attributes}
                      />
                    }
                    key={group + '-icon-' + index}
                  >
                    <LazyLoadComponent>
                      <Link onClick={() => onSelectAnalysis(item)}>
                        <GameIcon gameName={item.gameNames[marketIso]} src={item.url} />
                      </Link>
                    </LazyLoadComponent>
                  </GRTooltip>
                </Box>
              ))}
            </CardContent>
          </Card>
        )
      })}
    </Box>
  )
}

export default VisualIconsList
